import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { apiFetch, ApiError } from "../../lib/api";
import { photoUrl } from "../../lib/assets";
import { ADMIN_DASHBOARD_PATH } from "../../lib/adminPaths";

export default function AdminAboutSelfiesPage() {
  const { logout } = useAdminAuth();
  const [selfies, setSelfies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [error, setError] = useState("");

  const loadSelfies = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await apiFetch("/admin/about-selfies");
      setSelfies(body.data.selfies || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load selfies — try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadSelfies();
  }, [loadSelfies]);

  function handleFileChange(event) {
    setFiles(Array.from(event.target.files || []));
    setError("");
  }

  async function handleUpload(event) {
    event.preventDefault();
    if (files.length === 0) {
      setError("Choose one or more images first.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("photos", file);
      }

      await apiFetch("/admin/about-selfies/batch", {
        method: "POST",
        body: formData,
      });

      setFiles([]);
      event.target.reset();
      await loadSelfies();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed — try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this selfie? This can't be undone.")) {
      return;
    }

    setDeletingId(id);
    setError("");

    try {
      await apiFetch(`/admin/about-selfies/${id}`, { method: "DELETE" });
      await loadSelfies();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delete failed — try again.");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleMove(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= selfies.length) {
      return;
    }

    const nextSelfies = [...selfies];
    [nextSelfies[index], nextSelfies[targetIndex]] = [nextSelfies[targetIndex], nextSelfies[index]];

    const movedId = selfies[index].id;
    const previousSelfies = selfies;

    setReorderingId(movedId);
    setSelfies(nextSelfies);
    setError("");

    try {
      const body = await apiFetch("/admin/about-selfies/reorder", {
        method: "PATCH",
        body: JSON.stringify({
          order: nextSelfies.map((selfie) => selfie.id),
        }),
      });
      setSelfies(body.data.selfies || nextSelfies);
    } catch (err) {
      setSelfies(previousSelfies);
      setError(err instanceof ApiError ? err.message : "Couldn't reorder selfies — try again.");
    } finally {
      setReorderingId(null);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <Link className="admin-back-link" to={ADMIN_DASHBOARD_PATH}>
            ← Dashboard
          </Link>
          <p className="admin-eyebrow">ABOUT PAGE</p>
          <h1 className="admin-title admin-title--compact">Selfies over the years</h1>
        </div>
        <button className="admin-button admin-button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      <section className="admin-card admin-card--compact-upload">
        <form className="admin-upload-row" onSubmit={(event) => void handleUpload(event)}>
          <div className="admin-upload-row__field">
            <label className="admin-label" htmlFor="about-selfies">
              Upload images
            </label>
            <input
              className="admin-input"
              id="about-selfies"
              name="photos"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFileChange}
            />
          </div>
          <button
            className="admin-button admin-upload-row__button"
            type="submit"
            disabled={uploading || files.length === 0}
          >
            {uploading
              ? "Uploading…"
              : files.length > 1
                ? `Upload ${files.length} photos`
                : "Upload"}
          </button>
        </form>
        {error ? <p className="admin-error">{error}</p> : null}
      </section>

      <section className="admin-card" style={{ marginTop: 20 }}>
        <div className="admin-section-header">
          <div>
            <p className="admin-eyebrow">Collage</p>
            <h2 className="admin-tool-card__title">Selfies</h2>
          </div>
          <span className="admin-muted">{selfies.length} total</span>
        </div>

        {loading ? <p className="admin-muted">Loading selfies…</p> : null}

        {!loading && selfies.length === 0 ? (
          <p className="admin-muted">No selfies yet — upload images above.</p>
        ) : null}

        {!loading && selfies.length > 1 ? (
          <p className="admin-muted admin-muted--spaced">
            Use the arrows to change order on the About page collage.
          </p>
        ) : null}

        <ul className="admin-photo-list">
          {selfies.map((selfie, index) => (
            <li key={selfie.id} className="admin-photo-item">
              <div className="admin-photo-item__order">
                <span className="admin-photo-item__position">{index + 1}</span>
                <div className="admin-photo-item__order-buttons">
                  <button
                    className="admin-order-button"
                    type="button"
                    aria-label={`Move selfie ${index + 1} up`}
                    disabled={index === 0 || reorderingId !== null || deletingId !== null}
                    onClick={() => void handleMove(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="admin-order-button"
                    type="button"
                    aria-label={`Move selfie ${index + 1} down`}
                    disabled={
                      index === selfies.length - 1 || reorderingId !== null || deletingId !== null
                    }
                    onClick={() => void handleMove(index, 1)}
                  >
                    ↓
                  </button>
                </div>
              </div>
              <img
                className="admin-photo-item__thumb"
                src={photoUrl(selfie)}
                alt={`Selfie ${index + 1}`}
              />
              <div className="admin-photo-item__body">
                <p className="admin-muted">Position {index + 1}</p>
              </div>
              <div className="admin-photo-item__actions">
                <button
                  className="admin-button admin-button--ghost admin-button--danger"
                  type="button"
                  disabled={deletingId === selfie.id || reorderingId !== null}
                  onClick={() => void handleDelete(selfie.id)}
                >
                  {deletingId === selfie.id ? "Deleting…" : "Delete"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
